/**
 * El servidor decide el día nutricional con la zona horaria del perfil (RN-10). El cliente sólo
 * necesita nombrar días y horas en la zona del dispositivo, que es donde la persona vive.
 */

const time = new Intl.DateTimeFormat('es-AR', { hour: '2-digit', minute: '2-digit' });
const dayLong = new Intl.DateTimeFormat('es-AR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});
const dayShort = new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit' });

/** Un `DateOnly` del servidor: `2026-08-05`. Sin `Date`, para no correr un día por la zona. */
export function todayIso(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function shiftIsoDate(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  const shifted = new Date(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days);
  return todayIso(shifted);
}

/** `2026-08-05` a `Date` local, sin que el parser lo lea como UTC y retroceda un día. */
export function parseIsoDate(isoDate: string): Date {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}

export function formatTime(instant: string): string {
  return time.format(new Date(instant));
}

export function formatDayLong(isoDate: string): string {
  return dayLong.format(parseIsoDate(isoDate));
}

export function formatDayShort(isoDate: string): string {
  return dayShort.format(parseIsoDate(isoDate));
}

/** "Hoy" y "Ayer" ahorran una lectura; más atrás, la fecha dice más que el conteo de días. */
export function describeDay(isoDate: string, today: string = todayIso()): string {
  if (isoDate === today) {
    return 'Hoy';
  }

  if (isoDate === shiftIsoDate(today, -1)) {
    return 'Ayer';
  }

  return formatDayLong(isoDate);
}

/** Un `datetime-local` para el input, en la zona del dispositivo. */
export function toLocalInputValue(instant: string): string {
  const date = new Date(instant);
  const pad = (value: number) => `${value}`.padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/** El camino inverso: lo que el input devuelve, con el desplazamiento del dispositivo. */
export function fromLocalInputValue(value: string): string {
  return new Date(value).toISOString();
}
